/**
 * NPCs fighting for themselves (lane C0-NPC phase N2; design docs/design/C0_NPC_LIFE.html §05–06): small events all
 * over the plains — wolves round a woodcutter, bandits at a cart, boars at the paddy edge, a red name at the gate, a
 * disciple hurt at the training hall, a wanderer calling for help against a beast. Each has a tell (a cry, an icon
 * over the head, a ping on the minimap), plays out whether or not the player comes, and ends in a win, a loss (the
 * person hurt: home to rest a day; never dead) or thanks and a gift when the player helped.
 *
 * Data only: `systems/npcLifeEvents.ts` runs it (inside systems/npcLife.ts).
 */
import type { XY } from './layouts/plains';
import type { LifeActId, LifeJob } from './npcLife';

export type LifeEventId = 'wolves' | 'cart' | 'boars' | 'redGate' | 'hurtDisciple' | 'wanderer';

/** Someone made for an event (the carter and his escort, the gate guards, the wanderer). */
export interface EventActor {
  readonly name: string;
  readonly title: string;
  readonly sprite: string;
}

/** Where an actor event happens: metres on the 256 m map, or a landmark / a monster region's middle on any map. */
export type EventWhere =
  | { readonly big: XY }
  | { readonly mark: string; readonly dx?: number; readonly dy?: number }
  | { readonly region: string; readonly side?: 'near' };

export interface LifeEventDef {
  readonly name: string;
  /** It befalls one of the town's people doing one of these acts (their job among `jobs`)… */
  readonly victim?: { readonly jobs: readonly LifeJob[]; readonly acts: readonly LifeActId[] };
  /** …or people made for it, at `where` (the first is the one in trouble, the rest fight beside them). */
  readonly actors?: readonly EventActor[];
  readonly where?: readonly EventWhere[];
  /** What attacks (data/monsters.ts id) and how many; none: a mishap (the hurt disciple). */
  readonly foe?: { readonly monster: string; readonly n: number };
  /** The people's side: health (0–100 abstract), and how hard they hit back (their atk for the blow rules). */
  readonly fighter: { readonly hp: number; readonly atk: number };
  /** The cry (the tell), said at once and again while it lasts. */
  readonly cry: readonly string[];
  /** Said when they won on their own. */
  readonly won: readonly string[];
  /** Said when the player helped. */
  readonly thanks: readonly string[];
  /** Said going down. */
  readonly hurt: readonly string[];
  /** The gift for help: a good (data/professions.ts `GOODS`) and silver. */
  readonly gift: { readonly good?: string; readonly n?: number; readonly gold: number };
  /** Memory points for help: the one helped, their family and friends, the village. */
  readonly memory: { readonly victim: number; readonly ties: number; readonly town: number };
}

export const LIFE_EVENTS: Readonly<Record<LifeEventId, LifeEventDef>> = {
  wolves: {
    name: 'หมาป่าล้อมคนตัดไม้',
    victim: { jobs: ['woodcutter'], acts: ['chopWood'] },
    foe: { monster: 'wolf', n: 3 },
    fighter: { hp: 100, atk: 22 },
    cry: ['หมาป่า! ช่วยด้วย!', 'ใครก็ได้ช่วยที! หมาป่ามาเป็นฝูง!', 'ถอยไป! ขวานนี่คมนะ!'],
    won: ['ฮึ่ม… ไปซะ! ขวานข้ายังไม่ทื่อหรอก'],
    thanks: ['ขอบใจมากสหาย ถ้าไม่ได้เจ้า ข้าคงแย่ ไม้นี่รับไปเถอะ', 'บุญคุณนี้ข้าจำไว้ ไม้ดีๆ ข้าเก็บไว้ให้เจ้า'],
    hurt: ['โอ๊ย… ขาข้า… ต้องกลับไปพักก่อนแล้ว'],
    gift: { good: 'pine-log', n: 3, gold: 30 },
    memory: { victim: 30, ties: 5, town: 3 },
  },
  cart: {
    name: 'โจรดักปล้นเกวียน',
    actors: [
      { name: 'พ่อค้าเกวียนหลี่', title: 'พ่อค้าเกวียน', sprite: 'merchant' },
      { name: 'ยามคุ้มกันเผิง', title: 'ยามคุ้มกันเกวียน', sprite: 'watchman' },
      { name: 'ยามคุ้มกันอวี๋', title: 'ยามคุ้มกันเกวียน', sprite: 'guardOfficer' },
    ],
    where: [{ big: [128, 226] }, { region: 'bandits', side: 'near' }],
    foe: { monster: 'bandit', n: 4 },
    fighter: { hp: 100, atk: 26 },
    cry: ['โจร! โจรดักปล้น! ช่วยด้วย!', 'คุ้มกันเกวียนไว้! อย่าให้มันขนของไปได้!'],
    won: ['ไล่มันไปได้แล้ว… ของยังอยู่ครบ'],
    thanks: ['ท่านจอมยุทธ์ช่วยไว้ ส่วนแบ่งนี้รับไปเถิด', 'สินค้าครบ! ข้าจะเล่าให้ทั้งตลาดฟังว่าใครช่วยไว้'],
    hurt: ['เราสู้ไม่ไหวแล้ว… ถอยก่อน!'],
    gift: { good: 'iron-ingot', n: 2, gold: 60 },
    memory: { victim: 25, ties: 5, town: 5 },
  },
  boars: {
    name: 'หมูป่าบุกนา',
    victim: { jobs: ['farmer'], acts: ['plantRice', 'harvest', 'tillSoil', 'plough', 'fieldLunch'] },
    foe: { monster: 'boar', n: 2 },
    fighter: { hp: 100, atk: 20 },
    cry: ['หมูป่าบุกนาอีกแล้ว! ไล่มันไป!', 'ช่วยด้วย! ต้นข้าวข้าจะพังหมด!'],
    won: ['ไปเลย! อย่ามาอีกนะ!'],
    thanks: ['นาข้ารอดแล้ว! ข้าวใหม่ปีนี้แบ่งให้เจ้าด้วย', 'เนื้อหมูป่าคืนนี้ ข้าจะต้มให้กิน!'],
    hurt: ['โอย… ต้นข้าวพังหมด พรุ่งนี้คงได้ข้าวน้อย'],
    gift: { good: 'rice', n: 4, gold: 20 },
    memory: { victim: 25, ties: 5, town: 2 },
  },
  redGate: {
    name: 'หัวแดงบุกประตูเมือง',
    actors: [
      { name: 'ยามประตูจาง', title: 'ยามประตูเมือง', sprite: 'watchman' },
      { name: 'ยามประตูหวง', title: 'ยามประตูเมือง', sprite: 'guardOfficer' },
    ],
    where: [{ big: [210, 130] }, { mark: 'plaza', dx: 0, dy: -15 }],
    foe: { monster: 'knifer', n: 1 },
    fighter: { hp: 100, atk: 24 },
    cry: ['หัวแดงบุกเมือง! ชาวบ้านหลบไป!', 'หยุดตรงนั้น! ทิ้งอาวุธ!'],
    won: ['จับไม่ได้ แต่ไล่ไปได้ ปิดประตูให้ดี'],
    thanks: ['ขอบคุณท่านที่ช่วยรักษาประตูเมือง ค่าหัวนี่ของท่าน'],
    hurt: ['มันแรงเกิน… ส่งคนไปตามกำลังเสริม!'],
    gift: { gold: 120 },
    memory: { victim: 20, ties: 5, town: 5 },
  },
  hurtDisciple: {
    name: 'ศิษย์ซ้อมแล้วเจ็บ',
    victim: { jobs: ['disciple'], acts: ['spar', 'drillForm', 'horseStance'] },
    fighter: { hp: 100, atk: 0 },
    cry: ['โอ๊ย! ข้อเท้าข้า…', 'ลุกไม่ไหว… ใครช่วยพยุงที'],
    won: ['ครูพาข้าไปหาหมอแล้ว'],
    thanks: ['ขอบใจที่พยุงข้า ยานี่ครูให้มา แบ่งให้เจ้า', 'ไว้หายแล้วข้าจะประมือกับเจ้าให้เต็มที่'],
    hurt: ['คงต้องพักสักวัน…'],
    gift: { good: 'herb-leaf', n: 3, gold: 15 },
    memory: { victim: 20, ties: 10, town: 2 },
  },
  wanderer: {
    name: 'จอมยุทธ์พเนจรสู้สัตว์ร้าย',
    actors: [{ name: 'จอมยุทธ์เร่ร่อนเฟิงอี้', title: 'จอมยุทธ์พเนจร', sprite: 'heroJilegu' }],
    where: [{ big: [128, 66] }, { region: 'wolves' }],
    foe: { monster: 'bear', n: 1 },
    fighter: { hp: 100, atk: 30 },
    cry: ['ใครว่างมาช่วยหน่อย! หมีตัวนี้หนังเหนียวนัก!', 'ช่วยกันหน่อยสหาย! แบ่งของกันครึ่งๆ!'],
    won: ['ฮ่า! ล้มได้แล้ว ไม่ต้องช่วยก็ได้'],
    thanks: ['ร่วมมือกันดีนัก ของนี่แบ่งกันตามสัญญา', 'ฝีมือเจ้าไม่เลว ไว้ล่าด้วยกันอีกนะ'],
    hurt: ['แพ้แล้ว… พรุ่งนี้ข้าจะกลับมาใหม่'],
    gift: { good: 'wolf-meat', n: 2, gold: 50 },
    memory: { victim: 15, ties: 0, town: 2 },
  },
};

export const LIFE_EVENT_IDS = Object.keys(LIFE_EVENTS) as LifeEventId[];

/** The events' timing and fight rules. */
export const LIFE_EVENT = {
  /** The first event of a session, then the gap between two (real seconds): 4–6 an hour of play. */
  first: [200, 300] as const,
  gap: [540, 840] as const,
  /** The same event not again within this (seconds). */
  again: 600,
  /** An event the clock starts happens at least this far from the player (m): it is heard of and come to. */
  away: 22,
  /** The tell before the blows (s), and the longest an event lasts (s). */
  tell: 5,
  most: 90,
  /** Foes come out this far from the one in trouble (m). */
  foeAt: 6,
  /** A foe's blow: every so many seconds, after a wind-up, for this much of the 0–100 health. */
  foeEvery: [1.4, 2.2] as const,
  windup: 0.45,
  foeDamage: [7, 13] as const,
  /** The people's blows on a foe (through the blow rules, with their `atk`). */
  fighterEvery: 1.3,
  reach: 1.5,
  /** The hurt disciple: the player stands this close this long to help them up (m, s); else the teacher comes. */
  helpUpR: 1.8,
  helpUpT: 2,
  teacherAfter: 40,
  /** How long the hurt rest at home (real seconds: an in-game day). */
  injured: 3600,
  /** The player hears of an event this far off (m): a notice and the minimap ping. */
  heard: 90,
  /** Foes that outlive the event slink off after this (s), unless they are fighting the player. */
  linger: 12,
} as const;
